import { Fragment, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, PlayCircle, Undo2 } from 'lucide-react';

import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { StatusBadge } from '@/ui/StatusBadge';
import { cn } from '@/nucleo/utilitarios';

import type { ConfigDesvio, CredenciadoAteste } from '../tipos';
import { DrillOSsRows } from './DrillOSsRows';
import { formatarDataHora, rotuloTipoPessoa, STATUS_ATESTE } from './statusAteste';

interface CredenciadosTableProps {
  credenciados: CredenciadoAteste[];
  apenasDesvio: boolean;
  cfg: ConfigDesvio;
  calculandoIds: Set<string>;
  onRecalcular: (credenciadoId: string) => void;
  onDevolver: (credenciadoId: string, nome: string) => void;
}

const STATUS_QUE_TRAVAM_DEVOLUCAO: string[] = ['DEVOLVIDA'];

/** Nível 2 do drill-down: credenciados de uma DRE, com ações de ateste. */
export function CredenciadosTable({
  credenciados,
  apenasDesvio,
  cfg,
  calculandoIds,
  onRecalcular,
  onDevolver,
}: CredenciadosTableProps) {
  const [credAberto, setCredAberto] = useState<string | null>(null);

  const filtrados = apenasDesvio ? credenciados.filter((c) => c.desvio?.hasDesvio) : credenciados;

  if (credenciados.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Nenhuma medição nesta DRE para o mês.</div>
    );
  }
  if (apenasDesvio && filtrados.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Nenhum credenciado com desvio acima do limite nesta DRE.
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Credenciado</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Termo SEI</TableHead>
            <TableHead className="text-right">Apontamentos</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead>Desvio</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Calculado em</TableHead>
            <TableHead>Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtrados.map((c) => {
            const statusInfo = STATUS_ATESTE[c.statusMedicao];
            const aberto = credAberto === c.credenciadoId;
            const semApontamento = c.qtdApontamentos === 0;
            const isDevolvida = c.statusMedicao === 'DEVOLVIDA';
            const calculando = calculandoIds.has(c.credenciadoId);
            const podeRecalcular = !!c.medicaoId && !semApontamento && !calculando;
            const podeDevolver =
              !!c.medicaoId &&
              !semApontamento &&
              !STATUS_QUE_TRAVAM_DEVOLUCAO.includes(c.statusMedicao);

            return (
              <Fragment key={c.credenciadoId}>
                <TableRow className={isDevolvida ? 'bg-destructive/5' : undefined}>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setCredAberto(aberto ? null : c.credenciadoId)}
                      disabled={semApontamento}
                      aria-label="Expandir OSs"
                    >
                      {aberto ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="font-medium">{c.nome}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        c.tipo === 'PF'
                          ? 'outline'
                          : c.tipo === 'COOPERATIVA'
                            ? 'default'
                            : 'secondary'
                      }
                      className="text-[10px]"
                    >
                      {rotuloTipoPessoa(c.tipo)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{c.numeroSei}</TableCell>
                  <TableCell className="text-right">
                    {semApontamento ? (
                      <span className="text-xs text-destructive">sem dados</span>
                    ) : (
                      <span className="text-xs">{c.qtdApontamentos} dia(s)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <MoneyDisplay value={c.valorTotal} />
                  </TableCell>
                  <TableCell>
                    {c.desvio?.hasDesvio ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className="gap-1 border-warning text-[10px] text-warning"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {c.desvio.hasDesvioValor && c.desvio.pctValor !== null
                              ? `${c.desvio.pctValor >= 0 ? '+' : ''}${c.desvio.pctValor.toFixed(0)}%`
                              : null}
                            {c.desvio.hasDesvioAlunos && c.desvio.pctAlunos !== null && (
                              <span>
                                · {c.desvio.pctAlunos >= 0 ? '+' : ''}
                                {c.desvio.pctAlunos.toFixed(0)}% alunos
                              </span>
                            )}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1 text-xs">
                            {c.desvio.motivos.map((motivo) => (
                              <div key={motivo}>{motivo}</div>
                            ))}
                            <div className="border-t pt-1 text-muted-foreground">
                              Limites: {cfg.desvioValorPct}% valor · {cfg.desvioAlunosPct}% alunos ·
                              janela {cfg.baselineMeses} meses
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isDevolvida && c.motivoDevolucao ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={cn('cursor-help', 'inline-block')}>
                            <StatusBadge status={statusInfo.status} label={statusInfo.label} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          {c.motivoDevolucao}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <StatusBadge status={statusInfo.status} label={statusInfo.label} />
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.calculadoEm ? formatarDataHora(c.calculadoEm) : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={!podeRecalcular}
                        onClick={() => onRecalcular(c.credenciadoId)}
                      >
                        {calculando ? (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        ) : (
                          <PlayCircle className="h-3 w-3" aria-hidden />
                        )}
                        Recalcular
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-warning hover:text-warning"
                        disabled={!podeDevolver}
                        onClick={() => onDevolver(c.credenciadoId, c.nome)}
                        title="Devolver esta medição (trava só este item)"
                      >
                        <Undo2 className="h-3 w-3" aria-hidden />
                        <span className="sr-only">Devolver medição de {c.nome}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {aberto && <DrillOSsRows ordensServico={c.ordensServico} />}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}
